import { DateTime } from 'luxon'
import {
  LIMITE_DESCRICAO_NFSE,
  LIMITE_INFORMACOES_COMPLEMENTARES_NFSE,
  NFSE_STATUS_VIGENTES,
  RETENCAO_ISS_NAO_RETIDO,
  TRIBUTACAO_ISS_TRIBUTAVEL,
} from '../../../../constants/fiscal'
import {
  isCnpjValido,
  isCpfValido,
  normalizarCnpj,
  somenteDigitos,
} from '../../../../services/util/documento.service'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// LC 116, art. 8º-A: os subitens 7.02, 7.05 e 16.01 podem ter alíquota abaixo de 2%
const SUBITENS_SEM_ALIQUOTA_MINIMA = ['0702', '0705', '1601']

export function round2(valor) {
  return Math.round(valor * 100) / 100
}

export function toNum(valor) {
  if (valor === null || valor === undefined || valor === '') return null
  const numero = Number(valor)
  return Number.isFinite(numero) ? numero : null
}

export function formatarMoeda(valor) {
  const numero = toNum(valor)
  if (numero === null) return ''
  return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function formatarCodigoTributacaoNacional(codigo) {
  return String(codigo || '').replace(/^(\d{2})(\d{2})(\d{2})$/, '$1.$2.$3')
}

export function formatarCodigoNbs(codigo) {
  return String(codigo || '').replace(/^(\d)(\d{4})(\d{2})(\d{2})$/, '$1.$2.$3.$4')
}

export function isTransporteMunicipal(codigoTributacaoNacional) {
  return String(codigoTributacaoNacional || '').startsWith('16')
}

export function isNfseVigente(status) {
  return NFSE_STATUS_VIGENTES.includes(status)
}

// LC 116, art. 3º: transporte (item 16) e armazenagem (11.04) são tributados onde o serviço é executado
export function municipioIncidencia(
  codigoTributacaoNacional,
  municipioPrestacao,
  municipioEmissor,
) {
  const codigo = String(codigoTributacaoNacional || '')
  return codigo.startsWith('16') || codigo.startsWith('1104')
    ? municipioPrestacao
    : municipioEmissor
}

export function tomadorVazio(origem = 'empresa') {
  return {
    origem,
    company_id: null,
    client_id: null,
    tp_id: null,
    tipo_pessoa: 'PJ',
    cnpj_cpf: '',
    razao_social: '',
    inscricao_municipal: '',
    email: '',
    telefone: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    uf: null,
    city: null,
    cep: '',
  }
}

export function tomadorDoCadastro(dados, ufs) {
  return {
    ...tomadorVazio(dados.origem),
    company_id: dados.company_id || null,
    client_id: dados.client_id || null,
    tp_id: dados.tp_id || null,
    tipo_pessoa: dados.tipo_pessoa || 'PJ',
    cnpj_cpf: dados.cnpj_cpf || '',
    razao_social: dados.razao_social || '',
    inscricao_municipal: dados.inscricao_municipal || '',
    email: dados.email || '',
    telefone: dados.telefone || '',
    logradouro: dados.logradouro || '',
    numero: dados.numero || '',
    complemento: dados.complemento || '',
    bairro: dados.bairro || '',
    uf: ufs.find(uf => uf.uf === dados.uf) || null,
    city: dados.city_id ? { id: dados.city_id, name: dados.city_name || '' } : null,
    cep: dados.cep || '',
  }
}

// Tomador gravado numa NFS-e anterior (duplicar)
export function tomadorDaNfse(nfse, ufs) {
  return {
    ...tomadorVazio(nfse.tomador_origem),
    company_id: nfse.tomador_company_id,
    client_id: nfse.tomador_client_id,
    tp_id: nfse.tomador_tp_id,
    tipo_pessoa: nfse.tomador_tipo_pessoa,
    cnpj_cpf: nfse.tomador_cnpj_cpf || '',
    razao_social: nfse.tomador_razao_social || '',
    inscricao_municipal: nfse.tomador_inscricao_municipal || '',
    email: nfse.tomador_email || '',
    telefone: nfse.tomador_telefone || '',
    logradouro: nfse.tomador_logradouro || '',
    numero: nfse.tomador_numero || '',
    complemento: nfse.tomador_complemento || '',
    bairro: nfse.tomador_bairro || '',
    uf: ufs.find(uf => uf.uf === nfse.tomador_uf) || null,
    city: nfse.tomador_city_id
      ? { id: nfse.tomador_city_id, name: nfse.tomador_city_name || '' }
      : null,
    cep: nfse.tomador_cep || '',
  }
}

const MARCADOR = /\{(notas|placa|data|empresa)\}/g

export function resolverModeloDescricao(
  modelo,
  { notas = [], placa = '', data = null, empresa = '' },
) {
  const valores = {
    notas: notas.join(', '),
    placa: placa || '',
    data: data ? DateTime.fromJSDate(data).toFormat('dd/MM/yyyy') : '',
    empresa: empresa || '',
  }
  // Trecho entre vírgulas com marcador vazio sai (sem placa não sobra "veículo , em"); a frase principal fica
  const trechos = String(modelo || '').split(',')
  const mantidos = trechos.filter((trecho, indice) => {
    const marcadores = trecho.match(MARCADOR) || []
    return (
      indice === 0 ||
      !marcadores.length ||
      marcadores.some(marcador => valores[marcador.slice(1, -1)])
    )
  })
  return mantidos
    .join(',')
    .replace(MARCADOR, (marcador, chave) => valores[chave])
    .replace(/\s+,/g, ',')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export function baseCalculoIss(valores) {
  return round2(
    (toNum(valores.valor_servico) || 0) -
      (toNum(valores.desconto_incondicionado) || 0) -
      (toNum(valores.valor_deducao) || 0),
  )
}

export function valorIssEstimado(valores, aliquota) {
  const percentual = toNum(aliquota)
  if (percentual === null) return null
  return round2((baseCalculoIss(valores) * percentual) / 100)
}

export function erroAliquota(aliquota, codigoTributacaoNacional) {
  const semMinima = SUBITENS_SEM_ALIQUOTA_MINIMA.includes(
    String(codigoTributacaoNacional || '').slice(0, 4),
  )
  const abaixoDaMinima = semMinima ? aliquota <= 0 : aliquota < 2
  if (abaixoDaMinima || aliquota > 5) {
    return semMinima
      ? 'A alíquota do ISS deve ser maior que 0% e de no máximo 5%'
      : 'A alíquota do ISS deve ficar entre 2% e 5%'
  }
  return null
}

function erroTomador(tomador, cnpjEmissor) {
  if (!tomador) return 'Informe o tomador'
  if (tomador.origem === 'empresa' && !tomador.company_id) return 'Tomador: selecione a Empresa'
  if (tomador.origem === 'cliente' && !tomador.client_id) return 'Tomador: selecione o Cliente'
  if (tomador.origem === 'tp' && !tomador.tp_id) return 'Tomador: selecione o Operador Logístico'
  if (tomador.tipo_pessoa === 'PF' && !isCpfValido(tomador.cnpj_cpf)) return 'Tomador: CPF inválido'
  if (tomador.tipo_pessoa === 'PJ' && !isCnpjValido(tomador.cnpj_cpf))
    return 'Tomador: CNPJ inválido'
  if (
    cnpjEmissor &&
    tomador.tipo_pessoa === 'PJ' &&
    normalizarCnpj(tomador.cnpj_cpf) === normalizarCnpj(cnpjEmissor)
  )
    return 'O tomador não pode ser o próprio emissor da NFS-e'
  if (!String(tomador.razao_social || '').trim()) return 'Tomador: informe a Razão Social'
  const email = String(tomador.email || '').trim()
  if (email && !EMAIL_REGEX.test(email)) return 'Tomador: e-mail inválido'
  const telefone = somenteDigitos(tomador.telefone)
  if (telefone && (telefone.length < 6 || telefone.length > 20))
    return 'Tomador: o telefone deve ter de 6 a 20 dígitos'
  if (!String(tomador.logradouro || '').trim()) return 'Tomador: informe o Logradouro'
  if (!String(tomador.numero || '').trim()) return 'Tomador: informe o Número do endereço'
  if (!String(tomador.bairro || '').trim()) return 'Tomador: informe o Bairro'
  if (!tomador.city || !tomador.city.id) return 'Tomador: selecione a Cidade'
  if (somenteDigitos(tomador.cep).length !== 8) return 'Tomador: informe o CEP com 8 dígitos'
  return null
}

// O emissor do ambiente municipal só emite pelo sistema da própria prefeitura; o nacional atende qualquer município
export function erroCoberturaEmissor(emissor, municipioPrestacao) {
  if (!emissor || emissor.ambiente_nfse !== 'municipal' || !municipioPrestacao) return null
  if (emissor.city_id === municipioPrestacao.id) return null
  const cidade = emissor.city_name
    ? `${emissor.city_name}/${emissor.city_uf}`
    : 'cadastrada no emissor'
  return `O emissor usa o ambiente municipal de ${cidade} e só emite com prestação nesse município. Para outro município, entre em contato com o suporte`
}

export function erroFormularioNfse(form) {
  if (!form.emissor) return 'Selecione o Emissor'
  if (!form.servico) return 'Selecione o Serviço'
  if (!form.data_competencia) return 'Informe a data de competência'
  if (DateTime.fromJSDate(form.data_competencia) > DateTime.local().endOf('day'))
    return 'A data de competência não pode ser futura'
  if (!form.municipio_prestacao) return 'Selecione o município da prestação'
  const erroCobertura = erroCoberturaEmissor(form.emissor, form.municipio_prestacao)
  if (erroCobertura) return erroCobertura

  const valorServico = toNum(form.valores.valor_servico)
  if (!(valorServico > 0)) return 'Informe o valor do serviço'
  if (baseCalculoIss(form.valores) <= 0)
    return 'O desconto incondicionado e a dedução não podem zerar o valor do serviço'
  if ((toNum(form.valores.desconto_condicionado) || 0) > valorServico)
    return 'O desconto condicionado não pode passar do valor do serviço'

  if (form.tributacao_iss === TRIBUTACAO_ISS_TRIBUTAVEL) {
    const aliquota = toNum(form.aliquota)
    if (aliquota === null) {
      if (form.emissor.ambiente_nfse === 'municipal') return 'Informe a alíquota do ISS'
    } else {
      const erro = erroAliquota(aliquota, form.servico.codigo_tributacao_nacional)
      if (erro) return erro
    }
  } else if (form.tipo_retencao_iss !== RETENCAO_ISS_NAO_RETIDO) {
    return 'Sem ISS a recolher (imunidade, exportação ou não incidência), a retenção deve ser "Não retido"'
  }

  const descricao = String(form.descricao_servico || '').trim()
  if (!descricao) return 'Informe a Descrição do serviço'
  if (descricao.length > LIMITE_DESCRICAO_NFSE)
    return `A Descrição do serviço deve conter no máximo ${LIMITE_DESCRICAO_NFSE} caracteres`
  if (String(form.informacoes_complementares || '').length > LIMITE_INFORMACOES_COMPLEMENTARES_NFSE)
    return `As Informações complementares devem conter no máximo ${LIMITE_INFORMACOES_COMPLEMENTARES_NFSE} caracteres`

  return erroTomador(form.tomador, form.emissor.cnpj)
}

export function montarPayloadNfse(form, contexto) {
  const { tomador } = form
  const tributavel = form.tributacao_iss === TRIBUTACAO_ISS_TRIBUTAVEL
  return {
    emissor_id: form.emissor.id,
    servico_nfse_id: form.servico.id,
    deliveries_ids: form.notas.map(nota => nota.id),
    fleet_id: contexto.fleet_id || null,
    veiculo_placa: contexto.veiculo_placa || form.veiculo_placa || null,
    data_competencia: DateTime.fromJSDate(form.data_competencia).toFormat('yyyy-MM-dd'),
    municipio_prestacao_id: form.municipio_prestacao.id,
    descricao_servico: String(form.descricao_servico).trim(),
    informacoes_complementares: String(form.informacoes_complementares || '').trim() || null,
    valores: {
      valor_servico: toNum(form.valores.valor_servico),
      desconto_incondicionado: toNum(form.valores.desconto_incondicionado),
      desconto_condicionado: toNum(form.valores.desconto_condicionado),
      valor_deducao: toNum(form.valores.valor_deducao),
    },
    iss: {
      tributacao_iss: form.tributacao_iss,
      tipo_retencao_iss: form.tipo_retencao_iss,
      aliquota: tributavel ? toNum(form.aliquota) : null,
    },
    tomador: {
      origem: tomador.origem,
      company_id: tomador.company_id,
      client_id: tomador.client_id,
      tp_id: tomador.tp_id,
      tipo_pessoa: tomador.tipo_pessoa,
      cnpj_cpf:
        tomador.tipo_pessoa === 'PF'
          ? somenteDigitos(tomador.cnpj_cpf)
          : normalizarCnpj(tomador.cnpj_cpf),
      razao_social: String(tomador.razao_social).trim(),
      inscricao_municipal: String(tomador.inscricao_municipal || '').trim() || null,
      email: String(tomador.email || '').trim() || null,
      telefone: somenteDigitos(tomador.telefone) || null,
      logradouro: String(tomador.logradouro).trim(),
      numero: String(tomador.numero).trim(),
      complemento: String(tomador.complemento || '').trim() || null,
      bairro: String(tomador.bairro).trim(),
      city_id: tomador.city.id,
      cep: somenteDigitos(tomador.cep),
    },
  }
}
